import React, { useState } from 'react';
import {
  Table,
  palettes,
  SmallBody,
  Body,
  ColumnDefinition,
  RowDefinition,
  SmallTitle
} from '@indigo-ag/component-module';
import { JudicialAction, JudicialActionFlag } from '@indigo-ag/schema';

import i18next from 'core/i18n';
import {
  PhaseSection,
  PhaseSectionProps
} from 'features/brazil-grower/components/BrazilGrowerFinance/shared/PhaseSection';
import {
  JudicialActionsModal,
  JudicialActionsModalProps
} from './components/JudicialActionsModal/JudicialActionsModal';
import {
  createStylesHook,
  EnhancedExpansionPanel,
  GridContainer,
  GridContainerItem,
  IJudicialActionsInfo,
  judicialFlagsTranslations,
  utils
} from 'shared';
import { currency } from 'shared/utils';

export interface JudicialActionsProps
  extends Pick<PhaseSectionProps, 'disabled'>,
    Pick<JudicialActionsModalProps, 'updateCredit'> {
  judicialActionsInfo?: IJudicialActionsInfo;
}

const useStyles = createStylesHook({
  flagType: {
    borderRadius: '50%',
    height: '.4rem',
    width: '.4rem'
  }
});

interface JudicialActionsSectionSummaryProps extends Pick<JudicialActionsProps, 'judicialActionsInfo'> {}

interface MappedJudicialActions {
  totalActions?: number;
  totalCauses?: string;
}

type ColumnId = 'totalActions' | 'totalCauses';
const columnHeadCells: ColumnDefinition<MappedJudicialActions, ColumnId>[] = [
  {
    id: 'totalActions',
    header: i18next.t('totalJudicialActions'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={`${row.totalActions}`} />
  },
  {
    id: 'totalCauses',
    header: i18next.t('totalCauses'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={`${row.totalCauses}`} />
  }
];
const rowDefinition: RowDefinition<MappedJudicialActions, ColumnId> = {
  rowId: ({ row }) => `${row.totalActions}`
};

export const JudicialActionsSectionSummary: React.FunctionComponent<JudicialActionsSectionSummaryProps> = ({
  judicialActionsInfo
}) => {
  /**
   * Styles
   */
  const classes = useStyles();

  const getFlagColor = (flag?: JudicialActionFlag): string => {
    if (flag === JudicialActionFlag.RED) {
      return palettes.coastal.accent.red;
    } else if (flag === JudicialActionFlag.GREEN) {
      return palettes.coastal.accent.green;
    } else if (flag === JudicialActionFlag.YELLOW) {
      return palettes.coastal.accent.yellow;
    } else {
      return '';
    }
  };

  const tableData: MappedJudicialActions[] = [
    {
      totalActions: judicialActionsInfo?.judicialActions?.length,
      totalCauses: currency.formatBrazilCurrency(
        judicialActionsInfo?.judicialActions?.reduce((a, b) => a + (b.causeValue || 0), 0) || 0
      )
    }
  ];

  const getFlagIndicator = (judicialAction: JudicialAction) => {
    return (
      <div className="flex align-items-center mt-1">
        <i
          className={classes.flagType}
          style={{
            background: getFlagColor(judicialAction.flag)
          }}
        />
        <SmallBody style={{ marginLeft: 8 }}>
          {judicialAction.flag ? judicialFlagsTranslations[judicialAction.flag] : null}
        </SmallBody>
      </div>
    );
  };

  const getOpportunityGridItems = (judicialAction: JudicialAction): GridContainerItem[] => {
    return [
      {
        label: i18next.t('court'),
        value: judicialAction.court
      },
      {
        label: i18next.t('authorName'),
        value: judicialAction.authorName
      },
      {
        label: i18next.t('actionType'),
        value: judicialAction.actionType
      },
      {
        label: i18next.t('causeValue'),
        value: judicialAction.causeValue ? currency.formatBrazilCurrency(judicialAction.causeValue) : undefined
      },
      {
        label: i18next.t('processNumber'),
        value: judicialAction.processNumber
      },
      {
        label: i18next.t('distributionDate'),
        value: judicialAction.distributionDate ? utils.date.formatDate(judicialAction.distributionDate) : undefined
      },
      {
        label: i18next.t('note'),
        value: judicialAction.observations
      },
      {
        label: i18next.t('flag'),
        customField: judicialAction.flag ? (
          <div>
            <SmallTitle color="red">Flag</SmallTitle>
            {getFlagIndicator(judicialAction)}
          </div>
        ) : (
          undefined
        )
      }
    ];
  };

  return judicialActionsInfo?.judicialActions?.length ? (
    <div className="width-100">
      <Table
        label="Judicial Actions"
        rowDefinition={rowDefinition}
        columnDefinition={columnHeadCells}
        data={tableData}
      />
      {judicialActionsInfo.judicialActions.map((value, key) => (
        <EnhancedExpansionPanel
          key={`${value.processNumber}${key}`}
          title={
            <div className="flex align-items-center">
              <Body className="mr-3">
                {currency.formatBrazilCurrency(value.causeValue ? value.causeValue.toString() : '0')}
              </Body>
              {getFlagIndicator(value)}
            </div>
          }
        >
          <GridContainer columns={4} items={getOpportunityGridItems(value)} classes={{ container: 'width-100' }} />
        </EnhancedExpansionPanel>
      ))}
    </div>
  ) : null;
};

export const JudicialActionsSection: React.FunctionComponent<JudicialActionsProps> = ({
  judicialActionsInfo,
  updateCredit,
  ...phaseSectionProps
}) => {
  /**
   * State
   */
  const [modalOpen, setModalOpen] = useState(false);

  const closeModal = (): void => setModalOpen(false);

  const openModal = (): void => setModalOpen(true);

  return (
    <>
      <PhaseSection
        {...phaseSectionProps}
        open={!!judicialActionsInfo}
        onClickEdit={openModal}
        title={i18next.t('judicialActions')}
        renderSeparatorBefore
      >
        <JudicialActionsSectionSummary judicialActionsInfo={judicialActionsInfo} />
      </PhaseSection>
      <JudicialActionsModal
        closeModal={closeModal}
        open={modalOpen}
        judicialActionsInfo={judicialActionsInfo}
        updateCredit={updateCredit}
      />
    </>
  );
};
